import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  createTheme,
  IconButton,
  InputAdornment,
  TextField,
  ThemeProvider,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  Fade,
  Grid,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LoginIcon from "@mui/icons-material/Login";
import { useEffect, useState } from "react";
import { useAuthLogin } from "../../client/hooks/auth/auth.queries";
import CustomSnackbar from "../components/Message.Component";
import etiquetasImage from "../../assets/etiquetas/etiquetas_media_png.png";

export default function LoginPage() {
  const [user, setUser] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [performLogin, setPerformLogin] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const { data, isLoading, isError, error } = useAuthLogin(
    { email: user, password },
    performLogin
  );
  const [alert, setAlert] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const theme = createTheme({
    cssVariables: true,
    palette: {
      primary: {
        main: "#FF7323",
        light: "#FF9B5A",
        dark: "#D85A00",
        contrastText: "#FFFFFF",
      },
      secondary: {
        main: "#1A73E8",
        light: "#4285F4",
        dark: "#1557B0",
        contrastText: "#FFFFFF",
      },
      background: {
        default: "#FAFBFC",
        paper: "#FFFFFF",
      },
      text: {
        primary: "#1A1A1A",
        secondary: "#4A5568",
      },
    },
    typography: {
      fontFamily:
        '"Inter", "SF Pro Display", "Segoe UI", "Roboto", -apple-system, BlinkMacSystemFont, sans-serif',
      h1: {
        background: "linear-gradient(135deg, #FF7323 0%, #D85A00 100%)",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        backgroundClip: "text",
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            padding: "14px 28px",
            fontSize: "1rem",
            fontWeight: 600,
            textTransform: "none",
            boxShadow: "none",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: "0px 10px 25px rgba(0, 0, 0, 0.15)",
            },
          },
          containedPrimary: {
            background: "linear-gradient(135deg, #FF7323 0%, #D85A00 100%)",
            color: "#FFFFFF",
            "&:hover": {
              background: "linear-gradient(135deg, #D85A00 0%, #B84700 100%)",
              boxShadow: "0px 15px 35px rgba(255, 115, 35, 0.4)",
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              borderRadius: 12,
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#FF7323",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#FF7323",
              },
            },
            "& .MuiInputLabel-root.Mui-focused": {
              color: "#FF7323",
            },
          },
        },
      },
    },
  });

  useEffect(() => {
    setIsVisible(true);
  }, []);

  useEffect(() => {
    // Só executa lógica se performLogin estiver true (tentativa de login em andamento)
    if (performLogin) {
      if (!isLoading) {
        if (data?.success) {
          localStorage.setItem("isLogged", true);
          localStorage.setItem("firstLogin", true);
          localStorage.setItem("token", data?.token);
          if (data.organization_id != null) {
            localStorage.setItem("organization_id", data.organization_id);
          } else {
            localStorage.setItem("organization_id", null);
          }

          window.location.replace("/dashboard");
        }

        if (isError) {
          // Mensagem amigável e legível para erros de login
          setErrorMsg("Usuário ou senha incorretos. Verifique seus dados e tente novamente.");
          setAlert(true);
        }

        // Reseta performLogin apenas quando a operação termina (sucesso ou erro)
        setPerformLogin(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isError, data, performLogin]);
  const execPerformLogin = () => {
    if (user && password && !performLogin && !isLoading) {
      // Reset o estado de erro antes de uma nova tentativa
      setAlert(false);
      setErrorMsg("");
      // Ativa o login
      setPerformLogin(true);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      execPerformLogin();
    }
  };

  return (
    <ThemeProvider theme={theme}>
      {/* Background principal */}
      <Box
        sx={{
          minHeight: "100vh",
          background: `
            linear-gradient(135deg, 
              rgba(255, 115, 35, 0.1) 0%, 
              rgba(26, 115, 232, 0.08) 30%,
              rgba(255, 255, 255, 0.95) 70%, 
              rgba(255, 255, 255, 1) 100%
            )
          `,
          position: "relative",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            width: "20px",
            height: "100%",
            background:
              "linear-gradient(90deg, rgba(255, 115, 35, 0.4) 0%, rgba(255, 115, 35, 0.1) 50%, transparent 100%)",
            borderRadius: "0 15px 15px 0",
            zIndex: 1,
          },
          "&::after": {
            content: '""',
            position: "absolute",
            top: 0,
            right: 0,
            width: "20px",
            height: "100%",
            background:
              "linear-gradient(270deg, rgba(255, 115, 35, 0.4) 0%, rgba(255, 115, 35, 0.1) 50%, transparent 100%)",
            borderRadius: "15px 0 0 15px",
            zIndex: 1,
          },
        }}
      >
        {/* Footer degradê - parte inferior */}
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            width: "100%",
            height: "20px",
            background:
              "linear-gradient(0deg, rgba(255, 115, 35, 0.4) 0%, rgba(255, 115, 35, 0.1) 50%, transparent 100%)",
            borderRadius: "15px 15px 0 0",
            zIndex: 1,
          }}
        />

        {/* Header degradê - parte superior */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "20px",
            background:
              "linear-gradient(180deg, rgba(255, 115, 35, 0.4) 0%, rgba(255, 115, 35, 0.1) 50%, transparent 100%)",
            borderRadius: "0 0 15px 15px",
            zIndex: 1,
          }}
        />

        {/* Imagem decorativa do dashboard */}
        <Box
          component="img"
          src={require("../../assets/img_mainframe.png")}
          alt="Dashboard Preview"
          sx={{
            position: "absolute",
            top: { xs: "10%", md: "15%" },
            right: { xs: "-20%", md: "-10%", lg: "5%" },
            width: { xs: "70%", md: "50%", lg: "40%" },
            maxWidth: "600px",
            opacity: { xs: 0.1, md: 0.15, lg: 0.2 },
            zIndex: 0,
            transform: "rotate(15deg)",
            filter: "blur(2px) drop-shadow(0 0 10px rgba(255, 255, 255, 0.2))",
            pointerEvents: "none",
          }}
        />

        {/* Imagem de etiquetas decorativa */}
        <Box
          component="img"
          src={etiquetasImage}
          alt="Sistema de Etiquetas"
          sx={{
            position: "absolute",
            top: { xs: "60%", md: "70%" },
            left: { xs: "-10%", md: "5%", lg: "10%" },
            width: { xs: "40%", md: "25%", lg: "20%" },
            maxWidth: "300px",
            opacity: { xs: 0.08, md: 0.12, lg: 0.15 },
            zIndex: 0,
            transform: "rotate(-10deg)",
            filter: "blur(1px) drop-shadow(0 0 15px rgba(255, 115, 35, 0.3))",
            pointerEvents: "none",
          }}
        />

        {/* Segunda imagem de etiquetas menor */}
        <Box
          component="img"
          src={etiquetasImage}
          alt="Sistema de Etiquetas"
          sx={{
            position: "absolute",
            top: { xs: "20%", md: "25%" },
            left: { xs: "-5%", md: "2%", lg: "5%" },
            width: { xs: "25%", md: "15%", lg: "12%" },
            maxWidth: "180px",
            opacity: { xs: 0.05, md: 0.08, lg: 0.1 },
            zIndex: 0,
            transform: "rotate(25deg)",
            filter: "blur(2px) drop-shadow(0 0 8px rgba(26, 115, 232, 0.2))",
            pointerEvents: "none",
          }}
        />

        {/* Elementos decorativos geométricos */}
        <Box
          sx={{
            position: "absolute",
            top: -100,
            right: -100,
            width: 300,
            height: 300,
            background:
              "radial-gradient(circle, rgba(255, 115, 35, 0.12) 0%, transparent 70%)",
            borderRadius: "50%",
            zIndex: 0,
            animation: "float 6s ease-in-out infinite",
            "@keyframes float": {
              "0%, 100%": { transform: "translateY(0px) rotate(0deg)" },
              "50%": { transform: "translateY(-20px) rotate(180deg)" },
            },
          }}
        />

        <Box
          sx={{
            position: "absolute",
            bottom: -150,
            left: -150,
            width: 400,
            height: 400,
            background:
              "radial-gradient(circle, rgba(26, 115, 232, 0.1) 0%, transparent 70%)",
            borderRadius: "50%",
            zIndex: 0,
            animation: "float 8s ease-in-out infinite reverse",
          }}
        />

        {/* Pattern de fundo sutil */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              radial-gradient(circle at 1px 1px, rgba(255, 115, 35, 0.08) 1px, transparent 0)
            `,
            backgroundSize: "60px 60px",
            opacity: 0.4,
            zIndex: 0,
          }}
        />

        {/* Layout principal com Grid */}
        <Container
          maxWidth="xl"
          sx={{ position: "relative", zIndex: 1, height: "100vh" }}
        >
          <Grid container sx={{ height: "100%" }}>
            {/* Coluna da esquerda - Branding */}
            <Grid
              item
              xs={12}
              md={7}
              lg={7}
              sx={{
                display: { xs: "none", md: "flex" },
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "flex-start",
                pr: { md: 4, lg: 8 },
                position: "relative",
              }}
            >
              <Fade in={isVisible} timeout={1000}>
                <Box>
                  {/* Logo principal */}
                  <Box
                    component="img"
                    src={require("../../assets/wala.png")}
                    alt="Wala Smart Work"
                    sx={{
                      height: { md: 80, lg: 100 },
                      width: "auto",
                      mb: 4,
                      filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.1))",
                    }}
                  />

                  {/* Subtítulo */}
                  <Typography
                    variant="h4"
                    sx={{
                      fontSize: { md: "1.5rem", lg: "1.8rem" },
                      fontWeight: 500,
                      color: "text.secondary",
                      mb: 4,
                      lineHeight: 1.4,
                      maxWidth: "500px",
                    }}
                  >
                    Plataforma SaaS completa para automatizar e potencializar
                    seu e-commerce
                  </Typography>

                  {/* Features highlights */}
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    {[
                      "🚀 Automação inteligente de processos",
                      "📊 Analytics avançados em tempo real",
                      "🔗 Integração com principais marketplaces",
                      "⚡ Performance otimizada para vendas",
                    ].map((feature, index) => (
                      <Fade
                        key={index}
                        in={isVisible}
                        timeout={1200 + index * 200}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            fontSize: "1.1rem",
                            color: "text.secondary",
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                          }}
                        >
                          {feature}
                        </Typography>
                      </Fade>
                    ))}
                  </Box>
                </Box>
              </Fade>

              {/* Preview da dashboard como elemento decorativo */}
              <Box
                sx={{
                  position: "absolute",
                  bottom: { md: "-10%", lg: "0%" },
                  right: { md: "-20%", lg: "-10%" },
                  width: "60%",
                  opacity: 0.3,
                  transform: "rotate(-5deg)",
                  zIndex: -1,
                }}
              >
                <img
                  src={require("../../assets/img_mainframe.png")}
                  alt="Dashboard Preview"
                  style={{
                    width: "100%",
                    height: "auto",
                    borderRadius: "16px",
                    boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                  }}
                />
              </Box>
            </Grid>

            {/* Coluna da direita - Login Form */}
            <Grid
              item
              xs={12}
              md={5}
              lg={5}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                py: { xs: 4, md: 0 },
              }}
            >
              <Box sx={{ width: "100%", maxWidth: "480px" }}>
                <CustomSnackbar
                  open={alert}
                  onClose={() => setAlert(false)}
                  severity="error"
                  message={errorMsg}
                />

                <Fade in={isVisible} timeout={800}>
                  <Card
                    sx={{
                      borderRadius: "24px",
                      border: "2px solid rgba(255, 115, 35, 0.35)",
                      boxShadow: "0px 25px 60px rgba(0, 0, 0, 0.15)",
                      background: "rgba(255, 255, 255, 0.98)",
                      backdropFilter: "blur(20px)",
                      overflow: "visible",
                      position: "relative",
                      transform: isVisible
                        ? "translateY(0)"
                        : "translateY(30px)",
                      transition: "all 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  >
                    <CardContent sx={{ p: { xs: 4, md: 6 } }}>
                      {/* Header com logo/avatar */}
                      <Box sx={{ textAlign: "center", mb: 4 }}>
                        <Avatar
                          sx={{
                            width: 70,
                            height: 70,
                            background:
                              "linear-gradient(135deg, #FF7323 0%, #D85A00 100%)",
                            mx: "auto",
                            mb: 3,
                            boxShadow: "0px 8px 25px rgba(255, 115, 35, 0.3)",
                          }}
                        >
                          <LockOutlinedIcon sx={{ fontSize: 35 }} />
                        </Avatar>

                        {/* Badge de boas-vindas */}
                        <Chip
                          label="🔐 Área Restrita"
                          sx={{
                            mb: 3,
                            backgroundColor: "rgba(255, 115, 35, 0.1)",
                            color: theme.palette.primary.main,
                            fontWeight: 600,
                            fontSize: "0.9rem",
                            padding: "8px 16px",
                            borderRadius: "50px",
                            border: "1px solid rgba(255, 115, 35, 0.2)",
                          }}
                        />

                        <Typography
                          variant="h2"
                          sx={{
                            fontSize: { xs: "2rem", md: "2.2rem" },
                            fontWeight: 800,
                            mb: 2,
                            background:
                              "linear-gradient(135deg, #FF7323 0%, #D85A00 50%, #1A73E8 100%)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            backgroundClip: "text",
                          }}
                        >
                          Bem-Vindo
                        </Typography>

                        <Typography
                          variant="body1"
                          sx={{
                            color: theme.palette.text.secondary,
                            fontSize: "1rem",
                            lineHeight: 1.6,
                          }}
                        >
                          Acesse sua conta para continuar
                        </Typography>
                      </Box>

                      {/* Formulário */}
                      <Box component="form" sx={{ mt: 3 }}>
                        <Box sx={{ mb: 3 }}>
                          <TextField
                            label="Email"
                            type="email"
                            fullWidth
                            size="large"
                            value={user}
                            onChange={(e) => setUser(e.target.value)}
                            onKeyPress={handleKeyPress}
                            disabled={isLoading}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                backgroundColor: "rgba(255, 255, 255, 0.9)",
                                fontSize: "1rem",
                                padding: "4px",
                              },
                            }}
                          />
                        </Box>

                        <Box sx={{ mb: 4 }}>
                          <TextField
                            label="Senha"
                            type={showPassword ? "text" : "password"}
                            fullWidth
                            size="large"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            onKeyPress={handleKeyPress}
                            disabled={isLoading}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end" sx={{ pr: 1 }}>
                                  <IconButton
                                    onClick={() =>
                                      setShowPassword(!showPassword)
                                    }
                                    edge="end"
                                    sx={{
                                      color: theme.palette.primary.main,
                                      "&:hover": {
                                        backgroundColor:
                                          "rgba(255, 115, 35, 0.1)",
                                      },
                                    }}
                                  >
                                    {showPassword ? (
                                      <VisibilityOff />
                                    ) : (
                                      <Visibility />
                                    )}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                backgroundColor: "rgba(255, 255, 255, 0.9)",
                                fontSize: "1rem",
                                padding: "4px",
                              },
                            }}
                          />
                        </Box>

                        {/* Botão de login */}
                        <Button
                          fullWidth
                          variant="contained"
                          size="large"
                          onClick={execPerformLogin}
                          disabled={isLoading || !user || !password}
                          startIcon={
                            isLoading ? (
                              <CircularProgress size={20} color="inherit" />
                            ) : (
                              <LoginIcon />
                            )
                          }
                          sx={{
                            py: 2,
                            fontSize: "1.1rem",
                            fontWeight: 600,
                            borderRadius: "12px",
                            background:
                              "linear-gradient(135deg, #FF7323 0%, #D85A00 100%)",
                            "&:hover": {
                              background:
                                "linear-gradient(135deg, #D85A00 0%, #B84700 100%)",
                              transform: "translateY(-2px)",
                              boxShadow: "0px 8px 25px rgba(255, 115, 35, 0.4)",
                            },
                            "&:disabled": {
                              background: "rgba(0, 0, 0, 0.12)",
                              color: "rgba(0, 0, 0, 0.26)",
                              transform: "none",
                              boxShadow: "none",
                            },
                          }}
                        >
                          {isLoading ? "Entrando..." : "Entrar"}
                        </Button>
                      </Box>

                      {/* Indicadores de confiança */}
                      <Box sx={{ textAlign: "center", mt: 3 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: theme.palette.text.secondary,
                            fontSize: "0.85rem",
                            display: "block",
                          }}
                        >
                          ✅ Conexão segura • ✅ Dados protegidos • ✅ Suporte
                          24/7
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Fade>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </ThemeProvider>
  );
}
